import type { ReactNode } from 'react';
import { AlertTriangle, ArrowRight, Ban, Clock, ExternalLink, Hourglass, Link2, Timer } from 'lucide-react';
import {
  AGGREGATE_LABEL,
  formatAggregate,
  formatNumberOf,
  calendarDate,
  dueState,
  formatDue,
  localDateKey,
  PRIORITIES,
  safeLink,
  DIST_BY_TEXT,
  type DashCard,
  type DashFilter,
  type DashSet,
  type Widget,
  type WidgetData,
} from '@kaba/shared';
import { navigate } from '../../lib/router';
import { cx } from '../ui/ui';
import { CalendarBody } from './CalendarWidget';
import { BarList, catColor, Columns, DataTable, Donut, LABEL_HEX, Line, type BarRow } from './charts';

/** Position der Übersicht merken, damit „Zurück“ dort weitermacht (A08) */
export const SCROLL_KEY = 'kaba.dashScroll';
function openCard(c: Pick<DashCard, 'id' | 'boardId'>) {
  try {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
  } catch {
    /* egal */
  }
  navigate(`/b/${c.boardId}?card=${c.id}`);
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-3 text-sm text-[var(--ink-soft)]">{children}</p>;
}

const linkCls = 'inline-flex min-h-11 items-center gap-1 rounded-lg px-1 text-sm font-medium text-accent-fg hover:underline sm:min-h-9';

export function MoreLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className={linkCls}>
      {children} <ArrowRight size={14} aria-hidden />
    </button>
  );
}

function DueChip({ c }: { c: Pick<DashCard, 'dueAt' | 'dueHasTime'> }) {
  if (!c.dueAt) return null;
  const st = dueState(c.dueAt, c.dueHasTime, null);
  const today = calendarDate(c.dueAt, c.dueHasTime) === localDateKey(new Date());
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center gap-1 rounded px-1.5 text-xs whitespace-nowrap',
        st === 'overdue' ? 'bg-rose-600 text-white' : today ? 'bg-amber-300 text-amber-950' : 'text-[var(--ink-soft)]',
      )}
    >
      <Clock size={12} aria-hidden />
      <span className="sr-only">{st === 'overdue' ? 'Überfällig, Frist' : 'Frist'}</span>
      {formatDue(c.dueAt, c.dueHasTime)}
    </span>
  );
}

/** Eine Karte in einer Bausteinliste – öffnet die vorhandene Karte im Board */
export function CardRow({ c, extra, hideDue }: { c: DashCard; extra?: ReactNode; hideDue?: boolean }) {
  const prio = c.priority >= 3 ? PRIORITIES.find((p) => p.value === c.priority)?.label : null;
  return (
    <li>
      <a
        href={`/b/${c.boardId}?card=${c.id}`}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          openCard(c);
        }}
        className="-mx-2 flex min-h-12 items-start gap-2 rounded-lg px-2 py-2 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm break-words">{c.title}</span>
          <span className="block truncate text-xs text-[var(--ink-soft)]">
            {c.boardTitle} · {c.columnTitle}
          </span>
          {extra && <span className="mt-0.5 block text-xs text-[var(--ink-soft)]">{extra}</span>}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          {!hideDue && <DueChip c={c} />}
          {c.blocked && (
            <span className="inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">
              <Ban size={11} aria-hidden /> Blockiert
            </span>
          )}
          {prio && <span className="text-xs font-medium text-[var(--ink-soft)]">{prio}</span>}
        </span>
      </a>
    </li>
  );
}

const days = (n: number) => (n < 1 ? 'seit heute' : n < 2 ? 'seit 1 Tag' : `seit ${Math.floor(n)} Tagen`);
const ago = (iso: string) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.round(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.round(h / 24);
  return d === 1 ? 'gestern' : `vor ${d} Tagen`;
};
const hm = (min: number) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
};
const KIND_TEXT: Record<string, string> = {
  created: 'angelegt',
  moved: 'verschoben',
  completed: 'erledigt',
  reopened: 'wieder geöffnet',
  due: 'Termin geändert',
  assigned: 'zugewiesen',
  unassigned: 'Zuständigkeit entfernt',
  attachment: 'Datei',
  blocked: 'blockiert',
  unblocked: 'Blockade gelöst',
  archived: 'archiviert',
  restored: 'wiederhergestellt',
  renamed: 'umbenannt',
  owner: 'Verantwortung',
  automation: 'Automation',
  dependency: 'Abhängigkeit',
  comment: 'Kommentar',
};

function dayLabel(key: string): string {
  const now = new Date();
  const t = localDateKey(now);
  const tm = localDateKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  if (key === t) return 'Heute';
  if (key === tm) return 'Morgen';
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Säulen je Woche – eine Reihe, eine Achse; unbekannte Wochen grau gestrichelt, nie als 0 */
function Throughput({ weeks }: { weeks: Extract<WidgetData, { type: 'flow' }>['throughput'] }) {
  const max = Math.max(1, ...weeks.map((w) => w.count ?? 0));
  const n = weeks.length;
  const H = 72;
  return (
    <figure className="mt-2">
      <svg viewBox={`0 0 ${n * 12} ${H + 2}`} className="h-20 w-full" preserveAspectRatio="none" role="img" aria-label="Erledigte Karten je Woche">
        <line x1="0" x2={n * 12} y1={H + 1} y2={H + 1} stroke="var(--chart-grid)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {weeks.map((w, i) => {
          if (w.count === null)
            return (
              <rect key={w.weekStart} x={i * 12 + 2} y={H - 8} width="8" height="8" fill="none" stroke="var(--ink-soft)" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" opacity="0.5">
                <title>Woche ab {w.label}: unbekannt (vor Beginn der Daten)</title>
              </rect>
            );
          const h = Math.max(w.count ? 2 : 0, (w.count / max) * H);
          return (
            <rect key={w.weekStart} x={i * 12 + 2} y={H - h} width="8" height={h} rx="1.5" fill="var(--chart-1)" opacity={w.partial ? 0.55 : 1}>
              <title>
                Woche ab {w.label}: {w.count} erledigt{w.partial ? ' (angeschnittene Woche)' : ''}
              </title>
            </rect>
          );
        })}
      </svg>
      <figcaption className="flex justify-between text-xs text-[var(--ink-soft)]">
        <span>{weeks[0]?.label}</span>
        <span>Erledigt je Woche · höchstens {max}</span>
        <span>{weeks.at(-1)?.label}</span>
      </figcaption>
      <details className="mt-1 text-xs">
        <summary className="cursor-pointer text-[var(--ink-soft)]">Als Tabelle</summary>
        <table className="mt-1 w-full text-left">
          <thead>
            <tr>
              <th className="font-medium">Woche ab</th>
              <th className="font-medium">Erledigt</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.weekStart}>
                <td>{w.label}</td>
                <td>{w.count === null ? 'unbekannt' : `${w.count}${w.partial ? ' (angeschnitten)' : ''}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

function Stat({ value, label, hint, tone, onClick }: { value: ReactNode; label: string; hint?: string; tone?: 'danger' | 'warn'; onClick?: () => void }) {
  const inner = (
    <>
      <span className={cx('text-2xl font-semibold tabular-nums', tone === 'danger' && 'text-rose-700 dark:text-rose-300', tone === 'warn' && 'text-amber-800 dark:text-amber-300')}>{value}</span>
      <span className="flex items-center gap-1 text-sm">
        {tone === 'danger' && <AlertTriangle size={14} className="text-rose-600" aria-hidden />}
        {label}
      </span>
      {hint && <span className="text-xs text-[var(--ink-soft)]">{hint}</span>}
    </>
  );
  const cls = 'flex min-h-20 flex-col items-start justify-center gap-0.5 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-left';
  return onClick ? (
    <button type="button" onClick={onClick} className={cx(cls, 'hover:border-accent focus-visible:border-accent')}>
      {inner}
    </button>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

const pct = (a: number, n: number) => (n ? `${Math.round((a / n) * 100)} %` : '–');

export function WidgetBody({
  w,
  data,
  openSet,
  openMetric,
  openGroup,
  filter,
}: {
  w: Widget;
  data: WidgetData;
  openSet: (set: DashSet, total: number) => void;
  openMetric?: (group: string | null) => void;
  /** 0.87: Karten hinter einer Gruppe (Verteilung, Auslastung, Termintreue) */
  openGroup?: (key: string, label: string) => void;
  /** 0.87: Filter der Übersicht (Kalender blättert selbst) */
  filter?: DashFilter;
}) {
  switch (data.type) {
    case 'calendar':
      return w.type === 'calendar' && filter ? <CalendarBody w={w} data={data} filter={filter} /> : null;
    case 'distribution': {
      if (!data.total) return <Empty>Keine passenden Karten.</Empty>;
      const donut = w.type === 'distribution' && w.config.chart === 'donut';
      const rows: BarRow[] = data.groups.map((g, i) => ({
        key: g.key,
        label: g.label,
        value: g.count,
        text: `${g.count} · ${pct(g.count, data.total)}`,
        color: data.by === 'label' && g.color ? LABEL_HEX[g.color] : donut ? catColor(i) : undefined,
      }));
      if (data.other) rows.push({ key: '__other', label: 'Weitere', value: data.other, text: `${data.other} · ${pct(data.other, data.total)}`, color: 'var(--cat-other)' });
      const pick = openGroup ? (r: BarRow) => r.key !== '__other' && openGroup(r.key, r.label) : undefined;
      return (
        <div className="flex flex-col gap-2">
          {donut ? <Donut rows={rows} total={data.total} center={data.total === 1 ? 'Karte' : 'Karten'} onPick={pick} aria={`Verteilung nach ${DIST_BY_TEXT[data.by]}`} /> : <BarList rows={rows} onPick={pick} />}
          <p className="text-xs text-[var(--ink-soft)]">
            {data.total} {data.total === 1 ? 'Karte' : 'Karten'}
            {data.overlap && ' · Karten mit mehreren Zuständigen oder Labels zählen in jeder Gruppe'}
          </p>
          {donut && <DataTable head={['Gruppe', 'Karten', 'Anteil']} rows={rows.map((r) => [r.label, r.value, pct(r.value, data.total)])} />}
        </div>
      );
    }
    case 'trend': {
      const net = data.created - data.completed;
      const last = data.buckets.at(-1);
      const stock = w.type === 'trend' && w.config.stock;
      return (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-3 gap-2">
            <Stat value={data.created} label="Neu angelegt" />
            <Stat value={data.completed} label="Erledigt" />
            <Stat value={net > 0 ? `+${net}` : net} label={net > 0 ? 'Bestand wächst' : net < 0 ? 'Bestand sinkt' : 'Gleichstand'} tone={net > 0 ? 'warn' : undefined} />
          </div>
          <Columns
            buckets={data.buckets.map((b) => ({ label: b.label, values: [b.created, b.completed], partial: b.partial }))}
            series={[
              { name: 'Neu angelegt', color: 'var(--chart-1)' },
              { name: 'Erledigt', color: 'var(--chart-2)' },
            ]}
            aria={`Neu angelegte und erledigte Karten je ${data.interval === 'week' ? 'Woche' : 'Monat'}`}
          />
          {stock && last && (
            <div>
              <p className="text-xs font-medium">Offener Bestand · jetzt {last.open}</p>
              <Line points={data.buckets.map((b) => ({ label: b.label, value: b.open }))} aria="Offener Bestand am Ende jedes Abschnitts" />
            </div>
          )}
          <DataTable head={[data.interval === 'week' ? 'Woche ab' : 'Monat', 'Neu', 'Erledigt', 'Offen am Ende']} rows={data.buckets.map((b) => [`${b.label}${b.partial ? ' (laufend)' : ''}`, b.created, b.completed, b.open])} />
        </div>
      );
    }
    case 'punctuality': {
      const n = data.onTime + data.late;
      return (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-3 gap-2">
            <Stat value={pct(data.onTime, n)} label="Pünktlich" hint={`${data.onTime} von ${n}`} onClick={openGroup && data.onTime ? () => openGroup('onTime', 'Pünktlich erledigt') : undefined} />
            <Stat
              value={data.late}
              label="Verspätet"
              hint={data.lateMedian === null ? undefined : `im Median ${data.lateMedian.toLocaleString('de-DE')} T`}
              tone={data.late ? 'warn' : undefined}
              onClick={openGroup && data.late ? () => openGroup('late', 'Verspätet erledigt') : undefined}
            />
            <Stat value={data.overdueOpen} label="Jetzt überfällig" tone={data.overdueOpen ? 'danger' : undefined} onClick={data.overdueOpen ? () => openSet('overdue', data.overdueOpen) : undefined} />
          </div>
          {n > 0 && (
            <Columns
              stacked
              buckets={data.buckets.map((b) => ({ label: b.label, values: [b.onTime, b.late], partial: b.partial }))}
              series={[
                { name: 'Pünktlich', color: 'var(--chart-1)' },
                { name: 'Verspätet', color: 'var(--chart-2)' },
              ]}
              aria={`Pünktlich und verspätet erledigte Karten je ${data.interval === 'week' ? 'Woche' : 'Monat'}`}
            />
          )}
          {data.groups.length > 0 && <p className="text-xs font-medium">Pünktlich je {w.type === 'punctuality' && w.config.groupBy === 'board' ? 'Board' : 'Person'}</p>}
          {data.groups.length > 0 && (
            <BarList rows={data.groups.map((g) => ({ key: g.key, label: g.label, value: g.onTime + g.late ? g.onTime / (g.onTime + g.late) : 0, text: `${pct(g.onTime, g.onTime + g.late)}${g.late ? ` · ${g.late} zu spät` : ''}`, sub: `${g.onTime + g.late} mit Frist` }))} max={1} />
          )}
          <p className="text-xs text-[var(--ink-soft)]">
            {n ? `${n} erledigte Karten mit Frist` : 'Im Zeitraum wurde keine Karte mit Frist erledigt.'}
            {data.noDue > 0 && ` · ${data.noDue} ohne Frist zählen nicht mit`}
          </p>
          <DataTable head={[data.interval === 'week' ? 'Woche ab' : 'Monat', 'Pünktlich', 'Verspätet', 'Quote']} rows={data.buckets.map((b) => [`${b.label}${b.partial ? ' (laufend)' : ''}`, b.onTime, b.late, pct(b.onTime, b.onTime + b.late)])} />
        </div>
      );
    }
    case 'workload': {
      if (!data.people.length) return <Empty>Keine offenen Karten mit Zuständigen.</Empty>;
      const est = data.measure === 'estimate';
      const max = Math.max(1, ...data.people.map((p) => (est ? p.estimate : p.open)));
      return (
        <div className="flex flex-col gap-1">
          {!est && (
            <ul className="mb-1 flex flex-wrap gap-x-3 text-xs text-[var(--ink-soft)]" aria-label="Legende">
              <li className="flex items-center gap-1">
                <span aria-hidden className="size-2.5 rounded-sm" style={{ background: 'var(--chart-1)' }} /> In Arbeit
              </li>
              <li className="flex items-center gap-1">
                <span aria-hidden className="size-2.5 rounded-sm" style={{ background: 'var(--chart-3)' }} /> Noch nicht begonnen
              </li>
            </ul>
          )}
          <ul className="flex flex-col gap-0.5">
            {data.people.map((p) => {
              const inner = (
                <>
                  <span className="min-w-0 truncate text-left">{p.label}</span>
                  <span className="flex h-2.5 overflow-hidden rounded-full bg-black/5 dark:bg-white/10" aria-hidden>
                    {est ? (
                      <span className="h-full rounded-full" style={{ width: `${(p.estimate / max) * 100}%`, background: 'var(--chart-1)' }} />
                    ) : (
                      <>
                        <span className="h-full" style={{ width: `${(p.started / max) * 100}%`, background: 'var(--chart-1)' }} />
                        <span className={cx('h-full', p.started > 0 && 'border-l-2 border-[var(--panel)]')} style={{ width: `${((p.open - p.started) / max) * 100}%`, background: 'var(--chart-3)' }} />
                      </>
                    )}
                  </span>
                  <span className="text-right whitespace-nowrap tabular-nums">
                    {est ? hm(p.estimate) : p.open}
                    {p.overdue > 0 && <span className="ml-1 text-rose-700 dark:text-rose-300">· {p.overdue} überfällig</span>}
                  </span>
                </>
              );
              const grid = 'grid w-full grid-cols-[minmax(0,8rem)_1fr_auto] items-center gap-2 text-sm';
              const label = `${p.label}: ${p.open} offen, ${p.started} in Arbeit${p.overdue ? `, ${p.overdue} überfällig` : ''}${est ? `, geschätzt ${hm(p.estimate)}${p.withoutEstimate ? `, ${p.withoutEstimate} ohne Schätzung` : ''}` : ''}`;
              return (
                <li key={p.key}>
                  {openGroup ? (
                    <button type="button" className={cx(grid, 'min-h-9 rounded-md px-1 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]')} onClick={() => openGroup(p.key, p.label)} aria-label={`${label} – Karten anzeigen`} title={label}>
                      {inner}
                    </button>
                  ) : (
                    <div className={cx(grid, 'px-1')} title={label}>
                      {inner}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {est && data.people.some((p) => p.withoutEstimate) && <p className="text-xs text-[var(--ink-soft)]">Karten ohne Schätzung zählen beim Aufwand nicht mit ({data.people.reduce((a, p) => a + p.withoutEstimate, 0)} Zuordnungen).</p>}
          {data.overlap && <p className="text-xs text-[var(--ink-soft)]">Karten mit mehreren Zuständigen zählen bei jeder Person.</p>}
          {data.other > 0 && <p className="text-xs text-[var(--ink-soft)]">{data.other} weitere Personen nicht gezeigt.</p>}
          <DataTable head={['Person', 'Offen', 'In Arbeit', 'Überfällig', 'Geschätzt']} rows={data.people.map((p) => [p.label, p.open, p.started, p.overdue, hm(p.estimate)])} />
        </div>
      );
    }
    case 'timeStats': {
      const week = data.groupBy === 'week';
      return (
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-2xl font-semibold tabular-nums">{hm(data.minutes)}</p>
            <p className="text-sm text-[var(--ink-soft)]">
              {data.people === 'me' ? 'selbst gebucht' : 'von allen gebucht'} in den letzten {data.days} Tagen
              {data.running && (
                <span className="ml-1 inline-flex items-center gap-1 text-amber-800 dark:text-amber-300">
                  <Timer size={13} aria-hidden /> Timer läuft – vorläufig
                </span>
              )}
            </p>
          </div>
          {!data.minutes ? (
            <Empty>Im Zeitraum wurde keine Zeit gebucht.</Empty>
          ) : week ? (
            <Columns buckets={data.groups.map((g) => ({ label: g.label, values: [Math.round(g.minutes / 6) / 10], partial: g.partial }))} series={[{ name: 'Stunden', color: 'var(--chart-1)' }]} aria="Gebuchte Stunden je Woche" unit="h" />
          ) : (
            <BarList rows={data.groups.map((g) => ({ key: g.key, label: g.label, value: g.minutes, text: hm(g.minutes) }))} />
          )}
          {data.estimate && (
            <p className="text-sm">
              Schätzung und Ist bei {data.estimate.cards} erledigten {data.estimate.cards === 1 ? 'Karte' : 'Karten'}: {hm(data.estimate.estimated)} geschätzt, {hm(data.estimate.spent)} gebucht
              {data.estimate.estimated > 0 && (
                <span className={cx('ml-1 font-medium', data.estimate.spent > data.estimate.estimated && 'text-amber-800 dark:text-amber-300')}>
                  ({data.estimate.spent >= data.estimate.estimated ? '+' : ''}
                  {Math.round(((data.estimate.spent - data.estimate.estimated) / data.estimate.estimated) * 100)} %)
                </span>
              )}
            </p>
          )}
          {data.minutes > 0 && <DataTable head={[{ week: 'Woche ab', board: 'Board', person: 'Person', card: 'Karte' }[data.groupBy], 'Zeit']} rows={data.groups.map((g) => [g.label, hm(g.minutes)])} />}
        </div>
      );
    }

    case 'deadlines': {
      return (
        <div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat value={data.overdue} label="Überfällig" tone={data.overdue ? 'danger' : undefined} onClick={() => openSet('overdue', data.overdue)} />
            <Stat value={data.today} label="Heute fällig" tone={data.today ? 'warn' : undefined} onClick={() => openSet('today', data.today)} />
            <Stat value={data.week} label="Nächste 7 Tage" onClick={() => openSet('week', data.week)} />
            <Stat value={data.none} label="Ohne Frist" onClick={() => openSet('none', data.none)} />
          </div>
        </div>
      );
    }
    case 'tasks':
      return (
        <div>
          {data.items.length ? (
            <ul className="flex flex-col">
              {data.items.map((c) => (
                <CardRow key={c.id} c={c} />
              ))}
            </ul>
          ) : (
            <Empty>Keine passenden Aufgaben.</Empty>
          )}
          {data.total > data.items.length && <MoreLink onClick={() => openSet('open', data.total)}>Alle {data.total} anzeigen</MoreLink>}
        </div>
      );
    case 'blocked':
      return (
        <div className="flex flex-col gap-3">
          <div>
            <h4 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">
              <Ban size={13} aria-hidden /> Manuell blockiert ({data.manualTotal})
            </h4>
            {data.manual.length ? (
              <ul>
                {data.manual.map((x) => (
                  <CardRow
                    key={x.card.id}
                    c={{ ...x.card, blocked: false }}
                    extra={
                      <>
                        {x.reason ? `„${x.reason}“` : 'Ohne Grund'}
                        {x.sinceDays !== null && ` · ${days(x.sinceDays)}`} · Nächster Schritt: Blockade klären
                      </>
                    }
                  />
                ))}
              </ul>
            ) : (
              <Empty>Nichts manuell blockiert.</Empty>
            )}
            {data.manualTotal > data.manual.length && <MoreLink onClick={() => openSet('blocked', data.manualTotal)}>Alle {data.manualTotal} anzeigen</MoreLink>}
          </div>
          <div>
            <h4 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">
              <Hourglass size={13} aria-hidden /> Warten auf Vorgänger ({data.waitingTotal})
            </h4>
            {data.waiting.length ? (
              <ul>
                {data.waiting.map((x) => (
                  <CardRow key={x.card.id} c={x.card} extra={<>Wartet auf: {x.open.map((p) => p.title).join(', ')}</>} />
                ))}
              </ul>
            ) : (
              <Empty>Keine Karte wartet auf einen Vorgänger.</Empty>
            )}
            {data.waitingTotal > data.waiting.length && <MoreLink onClick={() => openSet('waiting', data.waitingTotal)}>Alle {data.waitingTotal} anzeigen</MoreLink>}
          </div>
        </div>
      );
    case 'ready':
      return (
        <div>
          {data.items.length ? (
            <ul>
              {data.items.map((x) => (
                <CardRow key={x.card.id} c={x.card} extra={x.handoverAt ? <>Übergabe: Vorgänger erledigt {ago(x.handoverAt)}</> : undefined} />
              ))}
            </ul>
          ) : (
            <Empty>Keine startbereiten Aufgaben.</Empty>
          )}
          {data.total > data.items.length && <MoreLink onClick={() => openSet('ready', data.total)}>Alle {data.total} anzeigen</MoreLink>}
        </div>
      );
    case 'agenda': {
      if (!data.events.length) return <Empty>Keine Termine in den nächsten {w.type === 'agenda' ? w.config.days : ''} Tagen.</Empty>;
      const groups = new Map<string, typeof data.events>();
      for (const e of data.events) {
        const k = calendarDate(e.at, e.hasTime);
        groups.set(k, [...(groups.get(k) ?? []), e]);
      }
      return (
        <div className="flex flex-col gap-2">
          {[...groups].map(([k, evs]) => (
            <div key={k}>
              <h4 className="text-xs font-semibold text-[var(--ink-soft)]">{dayLabel(k)}</h4>
              <ul>
                {evs.map((e) => (
                  <CardRow
                    key={`${e.card.id}-${e.kind}`}
                    c={e.card}
                    hideDue
                    extra={
                      <>
                        <span className={cx('mr-1 rounded px-1 font-medium', e.kind === 'due' ? 'bg-black/5 dark:bg-white/10' : 'bg-accent-soft')}>{e.kind === 'due' ? 'Frist' : 'Start'}</span>
                        {e.hasTime ? new Date(e.at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : 'ganztägig'}
                      </>
                    }
                  />
                ))}
              </ul>
            </div>
          ))}
          <p className="text-xs text-[var(--ink-soft)]">
            {data.cards} {data.cards === 1 ? 'Karte' : 'Karten'}
            {data.more > 0 && ` · ${data.more} weitere Termine`}
          </p>
        </div>
      );
    }
    case 'boards':
      if (!data.items.length)
        return (
          <Empty>
            {w.type === 'boards' && w.config.source === 'favorites'
              ? 'Noch keine Favoriten. Markiere Boards mit dem Stern.'
              : w.type === 'boards' && w.config.source === 'recent'
                ? 'Auf diesem Gerät wurde noch kein Board geöffnet.'
                : 'Keine Boards ausgewählt oder kein Zugriff mehr.'}
          </Empty>
        );
      return (
        <ul className="flex flex-col">
          {data.items.map((b) => (
            <li key={b.id}>
              <a
                href={`/b/${b.id}`}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.button !== 0) return;
                  e.preventDefault();
                  try {
                    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
                  } catch {
                    /* egal */
                  }
                  navigate(`/b/${b.id}`);
                }}
                className="-mx-2 flex min-h-12 flex-col justify-center rounded-lg px-2 py-2 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
              >
                <span className="text-sm font-medium break-words">{b.title}</span>
                <span className="text-xs text-[var(--ink-soft)]">
                  {b.open} offen
                  {b.blocked > 0 && ` · ${b.blocked} blockiert`}
                  {b.overdue > 0 && <span className="text-rose-700 dark:text-rose-300"> · {b.overdue} überfällig</span>}
                  {b.nextDue && ` · nächste Frist ${formatDue(b.nextDue.at, b.nextDue.hasTime)}`}
                </span>
              </a>
            </li>
          ))}
        </ul>
      );
    case 'flow':
      return (
        <div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat value={data.completed} label="Erledigt" hint={data.reopened ? `${data.reopened} wieder geöffnet` : 'Haken gesetzt'} />
            <Stat
              value={data.cycle.median === null ? '–' : `${data.cycle.median.toLocaleString('de-DE')} T`}
              label="Durchlaufzeit (Median)"
              hint={`${data.cycle.count} Karten${data.cycle.unknown ? `, ${data.cycle.unknown} nicht berechenbar` : ''}`}
            />
            <Stat value={data.openAge.median === null ? '–' : `${data.openAge.median.toLocaleString('de-DE')} T`} label="Alter in Arbeit (Median)" hint={`${data.openAge.count} Karten`} />
            <Stat value={data.wip} label="In Arbeit" hint={`aus ${data.boards} ${data.boards === 1 ? 'Board' : 'Boards'}`} />
          </div>
          <Throughput weeks={data.throughput} />
        </div>
      );
    case 'time':
      return (
        <div>
          <p className="text-2xl font-semibold tabular-nums">{hm(data.minutes)}</p>
          <p className="text-sm text-[var(--ink-soft)]">
            selbst gebucht in den letzten {data.days} Tagen
            {data.running && (
              <span className="ml-1 inline-flex items-center gap-1 text-amber-800 dark:text-amber-300">
                <Timer size={13} aria-hidden /> Timer läuft – vorläufig
              </span>
            )}
          </p>
          {data.byBoard.length > 0 && (
            <ul className="mt-2 text-sm">
              {data.byBoard.map((b) => (
                <li key={b.boardId} className="flex justify-between gap-2 py-0.5">
                  <span className="truncate">{b.title}</span>
                  <span className="tabular-nums text-[var(--ink-soft)]">{hm(b.minutes)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    case 'activity':
      return data.items.length ? (
        <ul>
          {data.items.map((x) => (
            <CardRow
              key={x.card.id}
              c={x.card}
              hideDue
              extra={
                <>
                  {ago(x.lastAt)} · {x.kinds.map((k) => KIND_TEXT[k] ?? k).slice(0, 3).join(', ')}
                  {x.count > 1 && ` (${x.count} Änderungen)`}
                  {x.actorIds.length > 0 && ` · ${x.actorIds.map((id) => data.people[id] ?? 'Ehemaliges Mitglied').join(', ')}`}
                </>
              }
            />
          ))}
        </ul>
      ) : (
        <Empty>Keine Änderungen im Zeitraum.</Empty>
      );
    case 'metric': {
      // 0.82: Kennzahl – große Zahl, Ziel als Balken, Aufteilung als Balkenliste
      // 0.86: Bezugsmenge („2 von 3 Karten mit Wert“), Zielrichtung, Hinweis auf Mehrfachzählung, Klick → Karten
      if (!data.field) return <Empty>Noch kein Feld zugeordnet. Unter „Anpassen“ → Einstellungen ein Feld je Board wählen.</Empty>;
      const f = { id: '', name: data.field.name, type: data.field.type, options: [], config: data.field.config };
      const agg = w.type === 'metric' ? w.config.agg : 'sum';
      const mode = (w.type === 'metric' && w.config.targetMode) || 'neutral';
      const fmt = (v: number | null, n: number) => (v === null ? '–' : (formatAggregate(f, { kind: agg, value: v, n: n || 1 }) ?? '–').replace(/^Σ |^Ø |^min |^max /, ''));
      const pct = data.target && data.value !== null ? Math.round((data.value / data.target) * 100) : null;
      const maxG = Math.max(1, ...data.groups.map((g) => Math.abs(g.value ?? 0)));
      const total = data.cards ?? data.n;
      const missing = agg === 'count' ? 0 : Math.max(0, total - data.n);
      const basis =
        agg === 'count'
          ? `${data.n} von ${total} ${total === 1 ? 'Karte' : 'Karten'} ausgefüllt`
          : missing
            ? `${data.n} von ${total} ${total === 1 ? 'Karte' : 'Karten'} mit Wert · ${missing} ohne Wert`
            : `${data.n} ${data.n === 1 ? 'Wert' : 'Werte'}`;
      const bar = mode === 'limit' ? ((pct ?? 0) > 100 ? 'bg-rose-500' : 'bg-emerald-500') : mode === 'reach' ? ((pct ?? 0) >= 100 ? 'bg-emerald-500' : 'bg-accent') : 'bg-accent';
      const targetText = mode === 'limit' ? 'der Obergrenze' : 'vom Ziel';
      const Big = openMetric ? 'button' : 'p';
      return (
        <div className="flex flex-col gap-3">
          <div>
            <Big
              {...(openMetric ? { type: 'button' as const, onClick: () => openMetric(null), 'aria-label': `${fmt(data.value, data.n)} – Karten anzeigen` } : {})}
              className={cx('text-left text-3xl font-semibold tabular-nums', openMetric && 'min-h-11 rounded-lg hover:text-accent-fg focus-visible:text-accent-fg')}
            >
              {fmt(data.value, data.n)}
            </Big>
            <p className="text-xs text-[var(--ink-soft)]">
              {AGGREGATE_LABEL[agg]} „{data.field.name}“ · {basis}
              {data.onlyLabels?.length ? ` · nur ${data.onlyLabels.join(', ')}` : ''}
            </p>
          </div>
          {data.target !== null && (
            <div>
              <div className="h-2.5 overflow-hidden rounded-full bg-black/10 dark:bg-white/10" role="img" aria-label={`${pct ?? 0} % ${targetText}`}>
                <div className={cx('h-full rounded-full', bar)} style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }} />
              </div>
              <p className="mt-1 text-xs text-[var(--ink-soft)]">
                {pct ?? 0} % {targetText} {formatNumberOf(f, data.target)}
                {mode === 'limit' && (pct ?? 0) > 100 ? ' – überschritten' : mode === 'reach' && (pct ?? 0) >= 100 ? ' – erreicht' : ''}
              </p>
            </div>
          )}
          {data.groups.length > 0 && (
            <ul className="flex flex-col gap-0.5">
              {data.groups.map((g) => {
                const row = (
                  <>
                    <span className="truncate text-left" title={g.label}>
                      {g.label}
                    </span>
                    <span className="h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/10" aria-hidden>
                      <span className="block h-full rounded-full bg-accent" style={{ width: `${Math.round((Math.abs(g.value ?? 0) / maxG) * 100)}%` }} />
                    </span>
                    <span className="text-right tabular-nums">{fmt(g.value, g.n)}</span>
                  </>
                );
                const grid = 'grid w-full grid-cols-[minmax(0,8rem)_1fr_auto] items-center gap-2 text-sm';
                return (
                  <li key={g.label}>
                    {openMetric ? (
                      <button type="button" className={cx(grid, 'min-h-9 rounded-md px-1 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]')} onClick={() => openMetric(g.label)} aria-label={`${g.label}: ${fmt(g.value, g.n)} – Karten anzeigen`}>
                        {row}
                      </button>
                    ) : (
                      <div className={cx(grid, 'px-1')}>{row}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {data.overlap && <p className="text-xs text-[var(--ink-soft)]">Eine Karte kann in mehreren Gruppen stehen – die Gruppen ergeben zusammen nicht die Summe.</p>}
        </div>
      );
    }
    case 'links': {
      const items = w.type === 'links' ? w.config.items : [];
      if (!items.length) return <Empty>Noch keine Verknüpfungen. Unter „Anpassen“ → Einstellungen hinzufügen.</Empty>;
      return (
        <ul className="flex flex-col">
          {items.map((l, i) => {
            const s = safeLink(l.url);
            if (!s) return null;
            return (
              <li key={i}>
                <a
                  href={s.href}
                  {...(s.external ? { target: '_blank', rel: 'noopener noreferrer nofollow' } : {})}
                  onClick={(e) => {
                    if (s.external || e.metaKey || e.ctrlKey || e.button !== 0) return;
                    e.preventDefault();
                    navigate(s.href);
                  }}
                  className="-mx-2 flex min-h-12 items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
                >
                  {s.external ? <ExternalLink size={15} className="shrink-0 text-[var(--ink-soft)]" aria-hidden /> : <Link2 size={15} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />}
                  <span className="min-w-0">
                    <span className="block text-sm font-medium break-words">{l.title}</span>
                    <span className="block truncate text-xs text-[var(--ink-soft)]">
                      {s.external ? new URL(s.href).host : 'In KaBa'}
                      {s.external && <span className="sr-only"> (öffnet in neuem Tab)</span>}
                    </span>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      );
    }
  }
}

/** Erläuterungen zu einem Baustein – nur auf Abruf (Info), nicht dauerhaft im Baustein */
export function widgetNotes(w: Widget, data: WidgetData | undefined): string[] {
  const notes: string[] = [];
  if (w.type === 'ready') notes.push('Zählt Karten, die noch nicht begonnen, nicht blockiert und deren Vorgänger alle erledigt sind.');
  if (w.type === 'deadlines') {
    notes.push('Zählt offene Karten; erledigt heißt: Haken gesetzt.');
    if (data?.type === 'deadlines') {
      const asOf = new Date(data.asOf);
      notes.push(`Stichtag ${asOf.toLocaleDateString('de-DE')}, ${asOf.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} · Zeitzone ${data.timeZone}.`);
    }
  }
  if (w.type === 'metric') notes.push('Rechnet über die Karten, die zu den Filtern passen; archivierte Karten zählen nicht. Felder werden je Board ausdrücklich zugeordnet; auf Wunsch zählen nur Karten mit bestimmten Labels. Karten ohne Wert zählen bei Summe und Durchschnitt nicht mit – ein Klick auf die Zahl oder eine Zeile zeigt die Karten dahinter.');
  if (w.type === 'calendar')
    notes.push(
      'Zeigt Karten mit Terminen im gewählten Zeitraum; erledigte nur, wenn eingestellt. Ziehen auf einen anderen Tag (oder im Zeitraster auf eine Uhrzeit) plant die Karte um – nur in Boards, die du bearbeiten darfst. Ohne Ziehen: Karte öffnen und den Termin dort ändern.',
    );
  if (w.type === 'distribution') notes.push('Zählt Karten, die zu den Filtern passen; archivierte Karten zählen nicht. Ein Klick auf eine Gruppe zeigt die Karten dahinter.');
  if (w.type === 'trend') notes.push('Neu = angelegt im Abschnitt, erledigt = Haken im Abschnitt. Der Bestand ist die Zahl offener Karten am Ende jedes Abschnitts (heutiger Stand der Haken; archivierte Karten zählen nicht).');
  if (w.type === 'punctuality') notes.push('Zählt Karten, die im Zeitraum erledigt wurden und eine Frist hatten. Pünktlich heißt: Haken bis zum Ende der Frist (ganztägig: bis Tagesende).');
  if (w.type === 'workload') notes.push('Offene Karten je zuständiger Person; in Arbeit = in einer Spalte, die als „in Arbeit“ zählt. Mit Aufwand: Summe der Schätzungen.');
  if (w.type === 'timeStats') notes.push('Gebuchte Zeiten aus der Zeiterfassung in den gewählten Boards. „Schätzung und Ist“ vergleicht bei erledigten Karten die Schätzung mit der gebuchten Zeit aller Personen.');
  if (w.type === 'tasks') notes.push(({ due: 'Sortiert nach Frist, dann Priorität.', priority: 'Sortiert nach Priorität, dann Frist.', updated: 'Sortiert nach letzter Änderung.' } as const)[w.config.sort]);
  return notes;
}
